import { useState, useEffect } from "react";
import {
  Copy,
  Check,
  Share2,
  ExternalLink,
  QrCode,
  DollarSign,
  TrendingUp,
  Users,
  MousePointerClick,
  ShieldCheck,
  CheckCircle2,
  Clock,
  ArrowRight,
  Sparkles,
  HelpCircle,
  Building2,
  Download,
  Send,
  LogOut,
  ChevronRight,
  Sliders,
  Award,
} from "lucide-react";
import {
  AffiliateProfile,
  ReferralTransaction,
  AffiliateStats,
  getStoredAffiliate,
  saveAffiliate,
  clearStoredAffiliate,
  getStoredTransactions,
  getAffiliateStats,
  generateReferralCode,
  buildReferralLink,
} from "@/lib/affiliate";

interface AffiliatePortalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: "links" | "performance" | "calculator" | "marketing";
}

export function AffiliatePortal({ isOpen, onClose, defaultTab = "links" }: AffiliatePortalProps) {
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [activeTab, setActiveTab] = useState<"links" | "performance" | "calculator" | "marketing">(defaultTab);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedPitch, setCopiedPitch] = useState<string | null>(null);

  // Registration form state
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regRole, setRegRole] = useState("Hospitality Consultant");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Login form state
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loginInput, setLoginInput] = useState("");

  // Payout dialog state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState<"bank" | "upi">("bank");
  const [payoutAccountName, setPayoutAccountName] = useState("");
  const [payoutAccountNumber, setPayoutAccountNumber] = useState("");
  const [payoutBankName, setPayoutBankName] = useState("");
  const [payoutIfsc, setPayoutIfsc] = useState("");
  const [payoutUpiId, setPayoutUpiId] = useState("");
  const [payoutRequested, setPayoutRequested] = useState(false);

  // Filter state for transactions
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Calculator state
  const [calcAmount, setCalcAmount] = useState<number>(20000);
  const [calcCurrency, setCalcCurrency] = useState<"INR" | "USD">("INR");

  useEffect(() => {
    const existing = getStoredAffiliate();
    if (existing) {
      setProfile(existing);
      if (existing.payoutDetails) {
        setPayoutAccountName(existing.payoutDetails.accountName || "");
        setPayoutAccountNumber(existing.payoutDetails.accountNumber || "");
        setPayoutBankName(existing.payoutDetails.bankName || "");
        setPayoutIfsc(existing.payoutDetails.ifscOrRouting || "");
        setPayoutUpiId(existing.payoutDetails.upiId || "");
      }
    }
  }, []);

  if (!isOpen) return null;

  const stats: AffiliateStats = getAffiliateStats();
  const transactions: ReferralTransaction[] = getStoredTransactions();

  const filteredTransactions = transactions.filter((t) => {
    if (statusFilter === "all") return true;
    return t.status === statusFilter;
  });

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName.trim() || !regEmail.trim()) return;

    setIsSubmitting(true);
    setTimeout(() => {
      const code = generateReferralCode(regName);
      const link = buildReferralLink(code);
      const newProfile: AffiliateProfile = {
        id: `AFF-${Date.now().toString().slice(-6)}`,
        name: regName.trim(),
        email: regEmail.trim(),
        phone: regPhone.trim(),
        role: regRole,
        referralCode: code,
        referralLink: link,
        createdAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      };
      saveAffiliate(newProfile);
      setProfile(newProfile);
      setIsSubmitting(false);
    }, 400);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const query = loginInput.trim().toUpperCase();
    if (!query) return;

    // Search or create
    const code = query.startsWith("TK-") ? query : `TK-${query.slice(0, 5)}`;
    const link = buildReferralLink(code);
    const mockProfile: AffiliateProfile = {
      id: `AFF-${Math.floor(1000 + Math.random() * 9000)}`,
      name: query.includes("@") ? query.split("@")[0] : "Hospitality Partner",
      email: query.includes("@") ? query : "partner@hospitality.com",
      role: "Hospitality Consultant",
      referralCode: code,
      referralLink: link,
      createdAt: "12 Oct 2026",
    };
    saveAffiliate(mockProfile);
    setProfile(mockProfile);
  };

  const handleCopyLink = () => {
    if (!profile) return;
    navigator.clipboard.writeText(profile.referralLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCode = () => {
    if (!profile) return;
    navigator.clipboard.writeText(profile.referralCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyPitch = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedPitch(id);
    setTimeout(() => setCopiedPitch(null), 2000);
  };

  const handleLogout = () => {
    clearStoredAffiliate();
    setProfile(null);
  };

  const handleSavePayout = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    const updated: AffiliateProfile = {
      ...profile,
      payoutMethod,
      payoutDetails: {
        accountName: payoutAccountName,
        accountNumber: payoutAccountNumber,
        bankName: payoutBankName,
        ifscOrRouting: payoutIfsc,
        upiId: payoutUpiId,
      },
    };
    saveAffiliate(updated);
    setProfile(updated);
    setPayoutRequested(true);
    setTimeout(() => {
      setPayoutRequested(false);
      setShowPayoutModal(false);
    }, 2500);
  };

  const shareViaWhatsApp = () => {
    if (!profile) return;
    const msg = encodeURIComponent(
      `Hello! I wanted to introduce you to That Korner — an AI dining concierge & restaurant intelligence platform (Chef Buddy & Chef Brain). You can explore it with a 15-day trial here: ${profile.referralLink} or use referral code "${profile.referralCode}".`
    );
    window.open(`https://api.whatsapp.com/send?text=${msg}`, "_blank");
  };

  const shareViaEmail = () => {
    if (!profile) return;
    const subject = encodeURIComponent("Introducing That Korner — AI Dining Concierge for Restaurants");
    const body = encodeURIComponent(
      `Hi,\n\nI thought of your restaurant when I came across That Korner. They've built conversational digital menus with Chef Buddy, real-time profitability analytics with Chef Brain, and floor intelligence with Korner Ask.\n\nYou can review their platform and start a 15-day trial directly through my referral link below:\n${profile.referralLink}\n\nReferral Code: ${profile.referralCode}\n\nBest regards,\n${profile.name}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const calcCommission = Math.round(calcAmount * 0.05);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5 md:p-8">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Dialog Window */}
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] text-bone shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
        {/* Top Header */}
        <div className="flex items-center justify-between border-b border-white/10 bg-[#12151a] px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-tk-red/15 text-tk-red">
              <Award className="h-4 w-4" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-[10px] uppercase tracking-[0.25em] text-tk-red">
                  Official Programme
                </span>
                <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-bone/60">
                  5% Commission
                </span>
              </div>
              <h2 className="text-base font-medium tracking-tight text-white">
                That Korner Affiliate Portal
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {profile && (
              <button
                onClick={handleLogout}
                title="Sign out of affiliate workspace"
                className="hidden items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-bone/60 transition hover:border-tk-red/50 hover:text-tk-red sm:inline-flex"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Switch Account</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-bone/70 transition hover:bg-white/10 hover:text-white"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto">
          {!profile ? (
            /* =================== REGISTRATION / LOGIN FORM =================== */
            <div className="mx-auto max-w-2xl px-6 py-10 sm:py-14">
              <div className="text-center">
                <span className="inline-block rounded-full bg-tk-red/10 px-3.5 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-tk-red">
                  {isLoginMode ? "Affiliate Log In" : "Free Registration · Instant Activation"}
                </span>
                <h3 className="mt-3 font-display text-2xl font-light tracking-tight text-white sm:text-3xl">
                  {isLoginMode
                    ? "Access Your Affiliate Workspace"
                    : "Become a That Korner Affiliate"}
                </h3>
                <p className="mt-2 text-sm text-bone/70">
                  {isLoginMode
                    ? "Enter your registered email or your unique referral code to open your dashboard."
                    : "Earn a 5% commission on the first eligible payment from every restaurant you introduce to That Korner."}
                </p>
              </div>

              {!isLoginMode ? (
                <form onSubmit={handleRegister} className="mt-8 space-y-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-bone/70">
                      Full Name <span className="text-tk-red">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Maya Chen"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-bone/30 transition focus:border-tk-red focus:outline-none focus:ring-1 focus:ring-tk-red"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-wider text-bone/70">
                        Work Email <span className="text-tk-red">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="maya@hospitality.com"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-bone/30 transition focus:border-tk-red focus:outline-none focus:ring-1 focus:ring-tk-red"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-wider text-bone/70">
                        WhatsApp / Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        placeholder="+971 50 123 4567"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-bone/30 transition focus:border-tk-red focus:outline-none focus:ring-1 focus:ring-tk-red"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-bone/70">
                      Your Industry Role
                    </label>
                    <select
                      value={regRole}
                      onChange={(e) => setRegRole(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-[#161a22] px-4 py-3 text-sm text-white transition focus:border-tk-red focus:outline-none focus:ring-1 focus:ring-tk-red"
                    >
                      <option value="Hospitality Consultant">Hospitality Consultant / F&B Advisor</option>
                      <option value="Restaurant Technology Partner">Restaurant Technology Partner / POS Reseller</option>
                      <option value="Agencies and Business Advisors">Agency / Marketing & Growth Advisor</option>
                      <option value="Industry Professionals and Creators">Industry Professional / Food & Hospitality Creator</option>
                      <option value="Independent Restaurateur">Independent Restaurateur / Operator</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs leading-relaxed text-bone/60">
                    <p className="flex items-center gap-2 font-medium text-bone/80">
                      <ShieldCheck className="h-4 w-4 text-tk-red" />
                      Programme Terms Guarantee:
                    </p>
                    <p className="mt-1">
                      Registration is 100% free with no minimum sales quotas. You earn a 5% commission on the first eligible payment collected from any business you attribute through your link or code.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-tk-red py-3.5 text-sm font-medium tracking-wide text-white transition hover:bg-tk-red-2 hover:shadow-[0_0_20px_rgba(240,31,14,0.4)] disabled:opacity-50"
                  >
                    {isSubmitting ? "Generating Your Referral Code..." : "Activate Affiliate Account & Generate Link"}
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setIsLoginMode(true)}
                      className="text-xs text-bone/60 transition hover:text-white"
                    >
                      Already registered? <span className="text-tk-red underline">Sign in to your dashboard</span>
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="mt-8 space-y-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-bone/70">
                      Email Address or Referral Code
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. maya@hospitality.com or TK-MAYA420"
                      value={loginInput}
                      onChange={(e) => setLoginInput(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-bone/30 transition focus:border-tk-red focus:outline-none focus:ring-1 focus:ring-tk-red"
                    />
                  </div>

                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-tk-red py-3.5 text-sm font-medium tracking-wide text-white transition hover:bg-tk-red-2 hover:shadow-[0_0_20px_rgba(240,31,14,0.4)]"
                  >
                    Sign In to Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setIsLoginMode(false)}
                      className="text-xs text-bone/60 transition hover:text-white"
                    >
                      Need an account? <span className="text-tk-red underline">Register for free in 30 seconds</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            /* =================== LOGGED IN DASHBOARD WORKSPACE =================== */
            <div className="p-6 md:p-8">
              {/* Profile Bar */}
              <div className="flex flex-col justify-between gap-4 rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent p-5 sm:flex-row sm:items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
                    <span className="text-xs uppercase tracking-wider text-bone/60">
                      Affiliate Active · {profile.role}
                    </span>
                  </div>
                  <h3 className="mt-1 text-xl font-medium text-white">
                    {profile.name}
                  </h3>
                  <p className="text-xs text-bone/50">
                    ID: {profile.id} · Member since {profile.createdAt}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowPayoutModal(true)}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-medium text-white transition hover:border-tk-red/50 hover:bg-tk-red/10"
                  >
                    <DollarSign className="h-4 w-4 text-tk-red" />
                    Payout Details
                  </button>
                  <button
                    onClick={shareViaWhatsApp}
                    className="flex items-center gap-2 rounded-xl bg-[#25D366]/20 px-4 py-2.5 text-xs font-medium text-[#25D366] transition hover:bg-[#25D366]/30"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Share on WhatsApp
                  </button>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="mt-6 flex border-b border-white/10 text-xs sm:text-sm">
                <button
                  onClick={() => setActiveTab("links")}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition ${
                    activeTab === "links"
                      ? "border-tk-red text-white"
                      : "border-transparent text-bone/60 hover:text-white"
                  }`}
                >
                  <Share2 className="h-4 w-4" />
                  Your Referral Links & Code
                </button>
                <button
                  onClick={() => setActiveTab("performance")}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition ${
                    activeTab === "performance"
                      ? "border-tk-red text-white"
                      : "border-transparent text-bone/60 hover:text-white"
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  Performance & Ledger
                </button>
                <button
                  onClick={() => setActiveTab("calculator")}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition ${
                    activeTab === "calculator"
                      ? "border-tk-red text-white"
                      : "border-transparent text-bone/60 hover:text-white"
                  }`}
                >
                  <Sliders className="h-4 w-4" />
                  Live 5% Calculator
                </button>
                <button
                  onClick={() => setActiveTab("marketing")}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition ${
                    activeTab === "marketing"
                      ? "border-tk-red text-white"
                      : "border-transparent text-bone/60 hover:text-white"
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                  Product Pitch Kit
                </button>
              </div>

              {/* TAB 1: REFERRAL LINKS & SHARING */}
              {activeTab === "links" && (
                <div className="mt-6 space-y-6">
                  {/* Link & Code Cards */}
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Unique Referral Link */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-[11px] font-medium uppercase tracking-[0.2em] text-tk-red">
                          Your Unique Referral Link
                        </span>
                        <span className="rounded bg-white/10 px-2 py-0.5 text-[10px] text-bone/70">
                          90-Day Cookie
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-bone/60">
                        Share this link with restaurant owners, hoteliers, or colleagues. Clicks and leads are automatically attributed to your account.
                      </p>

                      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 p-2.5">
                        <input
                          type="text"
                          readOnly
                          value={profile.referralLink}
                          className="flex-1 bg-transparent font-mono text-xs text-white focus:outline-none"
                        />
                        <button
                          onClick={handleCopyLink}
                          className="flex items-center gap-1.5 rounded-lg bg-tk-red px-3 py-1.5 text-xs font-medium text-white transition hover:bg-tk-red-2"
                        >
                          {copiedLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          {copiedLink ? "Copied" : "Copy"}
                        </button>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2 text-xs">
                        <button
                          onClick={shareViaWhatsApp}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-bone/80 transition hover:bg-white/10 hover:text-white"
                        >
                          WhatsApp
                        </button>
                        <button
                          onClick={shareViaEmail}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-bone/80 transition hover:bg-white/10 hover:text-white"
                        >
                          Email
                        </button>
                        <a
                          href={profile.referralLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-bone/80 transition hover:bg-white/10 hover:text-white"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Test Link
                        </a>
                      </div>
                    </div>

                    {/* Unique Referral Code */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-[11px] font-medium uppercase tracking-[0.2em] text-tk-red">
                          Your Unique Referral Code
                        </span>
                        <span className="rounded bg-white/10 px-2 py-0.5 text-[10px] text-bone/70">
                          Direct Entry
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-bone/60">
                        Businesses can enter this code during demo bookings, contact forms, or sales consultations to guarantee your attribution.
                      </p>

                      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-black/40 p-3">
                        <span className="font-mono text-lg font-bold tracking-widest text-white">
                          {profile.referralCode}
                        </span>
                        <button
                          onClick={handleCopyCode}
                          className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white transition hover:bg-white/10"
                        >
                          {copiedCode ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                          {copiedCode ? "Copied" : "Copy Code"}
                        </button>
                      </div>

                      {/* QR Code generator */}
                      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.015] p-3 text-xs text-bone/70">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white p-1">
                            {/* Stylized QR representation */}
                            <div className="grid h-full w-full grid-cols-4 gap-0.5 bg-black p-0.5">
                              <div className="bg-white" />
                              <div className="bg-white" />
                              <div className="bg-black" />
                              <div className="bg-white" />
                              <div className="bg-white" />
                              <div className="bg-black" />
                              <div className="bg-white" />
                              <div className="bg-black" />
                              <div className="bg-black" />
                              <div className="bg-white" />
                              <div className="bg-white" />
                              <div className="bg-white" />
                              <div className="bg-white" />
                              <div className="bg-black" />
                              <div className="bg-black" />
                              <div className="bg-white" />
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-white">In-Person QR Code</p>
                            <p className="text-[11px] text-bone/50">Show from phone or print on business cards</p>
                          </div>
                        </div>
                        <a
                          href={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
                            profile.referralLink
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[11px] text-tk-red hover:underline"
                        >
                          <Download className="h-3 w-3" />
                          View Full QR
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Quick Referral Process Overview */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-6">
                    <h4 className="font-display text-sm font-medium uppercase tracking-[0.2em] text-white">
                      How Your Referrals Convert
                    </h4>
                    <div className="mt-4 grid gap-4 sm:grid-cols-4">
                      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs">
                        <span className="font-mono font-bold text-tk-red">01</span>
                        <p className="mt-1 font-medium text-white">You Share</p>
                        <p className="mt-0.5 text-bone/60">Share your referral link or code with restaurant operators.</p>
                      </div>
                      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs">
                        <span className="font-mono font-bold text-tk-red">02</span>
                        <p className="mt-1 font-medium text-white">Lead Attributed</p>
                        <p className="mt-0.5 text-bone/60">Visitor books a demo or signs up; lead is tagged to you.</p>
                      </div>
                      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs">
                        <span className="font-mono font-bold text-tk-red">03</span>
                        <p className="mt-1 font-medium text-white">First Payment</p>
                        <p className="mt-0.5 text-bone/60">Customer subscribes & completes their first eligible payment.</p>
                      </div>
                      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs">
                        <span className="font-mono font-bold text-tk-red">04</span>
                        <p className="mt-1 font-medium text-white">5% Payout</p>
                        <p className="mt-0.5 text-bone/60">Your 5% commission is recorded and paid to your account.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: PERFORMANCE & LEDGER */}
              {activeTab === "performance" && (
                <div className="mt-6 space-y-6">
                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex items-center justify-between text-bone/60">
                        <span className="text-xs">Referral Clicks</span>
                        <MousePointerClick className="h-4 w-4 text-tk-red" />
                      </div>
                      <p className="mt-2 text-2xl font-bold text-white">{stats.clicks}</p>
                      <p className="mt-1 text-[11px] text-emerald-400">Unique visitors</p>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex items-center justify-between text-bone/60">
                        <span className="text-xs">Attributed Leads</span>
                        <Users className="h-4 w-4 text-tk-red" />
                      </div>
                      <p className="mt-2 text-2xl font-bold text-white">{stats.leads}</p>
                      <p className="mt-1 text-[11px] text-bone/50">Demos & signups</p>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex items-center justify-between text-bone/60">
                        <span className="text-xs">Paying Customers</span>
                        <Building2 className="h-4 w-4 text-tk-red" />
                      </div>
                      <p className="mt-2 text-2xl font-bold text-white">{stats.conversions}</p>
                      <p className="mt-1 text-[11px] text-emerald-400">Converted referrals</p>
                    </div>

                    <div className="rounded-xl border border-tk-red/30 bg-tk-red/10 p-4">
                      <div className="flex items-center justify-between text-tk-red">
                        <span className="text-xs font-medium">Total Earned</span>
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <p className="mt-2 text-2xl font-bold text-white">
                        ₹{stats.totalEarned.toLocaleString("en-IN")}
                      </p>
                      <p className="mt-1 text-[11px] text-tk-cream/70">5% commission volume</p>
                    </div>
                  </div>

                  {/* Status Breakdown Pills */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                      <div>
                        <h4 className="text-xs font-medium uppercase tracking-wider text-bone/70">
                          Commission Pipeline Breakdown
                        </h4>
                        <p className="text-xs text-bone/50">
                          Track your commissions across all lifecycle statuses as described in the programme guidelines.
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {stats.payable > 0 && (
                          <button
                            onClick={() => setShowPayoutModal(true)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-500"
                          >
                            Withdraw ₹{stats.payable.toLocaleString("en-IN")}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                      <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs">
                        <div className="flex items-center gap-1.5 text-amber-300">
                          <Clock className="h-3 w-3" />
                          <span className="font-medium">Pending</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-white">
                          ₹{stats.pending.toLocaleString("en-IN")}
                        </p>
                        <p className="text-[10px] text-bone/50">Under 14-day clearance</p>
                      </div>

                      <div className="rounded-xl border border-blue-500/20 bg-blue-500/10 p-3 text-xs">
                        <div className="flex items-center gap-1.5 text-blue-300">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="font-medium">Approved</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-white">
                          ₹{stats.approved.toLocaleString("en-IN")}
                        </p>
                        <p className="text-[10px] text-bone/50">Cleared verification</p>
                      </div>

                      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/15 p-3 text-xs">
                        <div className="flex items-center gap-1.5 text-emerald-300">
                          <DollarSign className="h-3 w-3" />
                          <span className="font-medium">Payable</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-white">
                          ₹{stats.payable.toLocaleString("en-IN")}
                        </p>
                        <p className="text-[10px] text-bone/50">Ready for payout</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs">
                        <div className="flex items-center gap-1.5 text-bone/70">
                          <ShieldCheck className="h-3 w-3" />
                          <span className="font-medium">Paid</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-white">
                          ₹{stats.paid.toLocaleString("en-IN")}
                        </p>
                        <p className="text-[10px] text-bone/50">Disbursed to bank</p>
                      </div>

                      <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-xs">
                        <div className="flex items-center gap-1.5 text-rose-300">
                          <span>Reversed</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-white">
                          ₹{stats.reversed.toLocaleString("en-IN")}
                        </p>
                        <p className="text-[10px] text-bone/50">Refunds / Chargebacks</p>
                      </div>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                      <div>
                        <h4 className="text-sm font-medium text-white">
                          Referral Transaction History
                        </h4>
                        <p className="text-xs text-bone/50">
                          Itemized records of eligible first payments and your 5% commissions.
                        </p>
                      </div>

                      {/* Filter Pills */}
                      <div className="flex flex-wrap gap-1 text-xs">
                        {["all", "pending", "approved", "payable", "paid", "reversed"].map((st) => (
                          <button
                            key={st}
                            onClick={() => setStatusFilter(st)}
                            className={`rounded-lg px-2.5 py-1 capitalize transition ${
                              statusFilter === st
                                ? "bg-tk-red text-white"
                                : "bg-white/5 text-bone/60 hover:text-white"
                            }`}
                          >
                            {st}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-white/10 text-bone/50">
                            <th className="pb-3 font-medium">Business / Customer</th>
                            <th className="pb-3 font-medium">Outlets</th>
                            <th className="pb-3 font-medium">Eligible Payment</th>
                            <th className="pb-3 font-medium">Commission (5%)</th>
                            <th className="pb-3 font-medium">Status</th>
                            <th className="pb-3 font-medium">Date</th>
                            <th className="pb-3 font-medium">Invoice Ref</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {filteredTransactions.map((tx) => {
                            let badgeStyle = "bg-white/10 text-bone";
                            if (tx.status === "paid") badgeStyle = "bg-emerald-500/20 text-emerald-300";
                            if (tx.status === "payable") badgeStyle = "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30";
                            if (tx.status === "approved") badgeStyle = "bg-blue-500/20 text-blue-300";
                            if (tx.status === "pending") badgeStyle = "bg-amber-500/20 text-amber-300";
                            if (tx.status === "reversed") badgeStyle = "bg-rose-500/20 text-rose-300";

                            return (
                              <tr key={tx.id} className="transition hover:bg-white/[0.02]">
                                <td className="py-3 font-medium text-white">{tx.businessName}</td>
                                <td className="py-3 text-bone/70">{tx.outletCount}</td>
                                <td className="py-3 font-mono text-bone/90">
                                  ₹{tx.paymentAmount.toLocaleString("en-IN")}
                                </td>
                                <td className="py-3 font-mono font-bold text-white">
                                  ₹{tx.commissionAmount.toLocaleString("en-IN")}
                                </td>
                                <td className="py-3">
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${badgeStyle}`}>
                                    {tx.status}
                                  </span>
                                </td>
                                <td className="py-3 text-bone/60">{tx.date}</td>
                                <td className="py-3 font-mono text-bone/50">{tx.invoiceRef}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: COMMISSION CALCULATOR */}
              {activeTab === "calculator" && (
                <div className="mt-6 space-y-6">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
                    <span className="font-display text-[11px] font-medium uppercase tracking-[0.2em] text-tk-red">
                      Official Commission Calculator
                    </span>
                    <h4 className="mt-1 font-display text-xl font-light text-white sm:text-2xl">
                      Simulate Your 5% Referral Commission
                    </h4>
                    <p className="mt-1 text-xs text-bone/60">
                      Based on That Korner's transparent commission structure: 5% of the first eligible payment actually collected.
                    </p>

                    <div className="mt-8 grid gap-8 md:grid-cols-2">
                      <div className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium uppercase tracking-wider text-bone/70">
                              Customer's First Eligible Payment
                            </label>
                            <span className="font-mono text-base font-bold text-tk-red">
                              ₹{calcAmount.toLocaleString("en-IN")}
                            </span>
                          </div>

                          <input
                            type="range"
                            min="5000"
                            max="200000"
                            step="5000"
                            value={calcAmount}
                            onChange={(e) => setCalcAmount(Number(e.target.value))}
                            className="mt-3 w-full accent-tk-red cursor-pointer"
                          />

                          <div className="mt-2 flex justify-between text-[10px] text-bone/40">
                            <span>₹5,000</span>
                            <span>₹20,000 (PDF Example)</span>
                            <span>₹100,000</span>
                            <span>₹200,000</span>
                          </div>
                        </div>

                        {/* Quick preset buttons */}
                        <div>
                          <span className="text-xs text-bone/50">Common Restaurant Scenarios:</span>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <button
                              type="button"
                              onClick={() => setCalcAmount(20000)}
                              className={`rounded-lg border px-3 py-1.5 transition ${
                                calcAmount === 20000
                                  ? "border-tk-red bg-tk-red/10 text-white"
                                  : "border-white/10 text-bone/70 hover:text-white"
                              }`}
                            >
                              ₹20,000 (1 Outlet Starter)
                            </button>
                            <button
                              type="button"
                              onClick={() => setCalcAmount(50000)}
                              className={`rounded-lg border px-3 py-1.5 transition ${
                                calcAmount === 50000
                                  ? "border-tk-red bg-tk-red/10 text-white"
                                  : "border-white/10 text-bone/70 hover:text-white"
                              }`}
                            >
                              ₹50,000 (Multi-Outlet)
                            </button>
                            <button
                              type="button"
                              onClick={() => setCalcAmount(120000)}
                              className={`rounded-lg border px-3 py-1.5 transition ${
                                calcAmount === 120000
                                  ? "border-tk-red bg-tk-red/10 text-white"
                                  : "border-white/10 text-bone/70 hover:text-white"
                              }`}
                            >
                              ₹120,000 (Hotel / Group)
                            </button>
                          </div>
                        </div>

                        <div className="rounded-xl border border-white/5 bg-white/[0.015] p-4 text-xs text-bone/60">
                          <p className="font-medium text-bone/80">Commission Formula:</p>
                          <p className="mt-1 font-mono text-[11px] text-bone/70">
                            Eligible Payment (₹{calcAmount.toLocaleString("en-IN")}) × 5% = ₹{calcCommission.toLocaleString("en-IN")}
                          </p>
                          <p className="mt-2 text-[10px] text-bone/50">
                            *Excludes taxes, refunds, cancellations, and chargebacks. Paid on the first eligible payment only. Renewals are excluded.
                          </p>
                        </div>
                      </div>

                      {/* Result Box */}
                      <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-gradient-to-br from-tk-red/15 via-white/[0.02] to-transparent p-6 text-center">
                        <div>
                          <span className="text-xs uppercase tracking-widest text-bone/60">
                            Your Calculated Referral Commission
                          </span>
                          <div className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
                            ₹{calcCommission.toLocaleString("en-IN")}
                          </div>
                          <p className="mt-1 text-xs text-emerald-400 font-medium">
                            5% direct commission on this single referral
                          </p>
                        </div>

                        <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-4 text-left text-xs">
                          <div className="flex items-center justify-between border-b border-white/5 pb-2">
                            <span className="text-bone/60">If you refer 5 restaurants:</span>
                            <span className="font-bold text-white">
                              ₹{(calcCommission * 5).toLocaleString("en-IN")}
                            </span>
                          </div>
                          <div className="flex items-center justify-between border-b border-white/5 py-2">
                            <span className="text-bone/60">If you refer 12 restaurants:</span>
                            <span className="font-bold text-white">
                              ₹{(calcCommission * 12).toLocaleString("en-IN")}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2">
                            <span className="text-bone/60">If you refer 25 restaurants:</span>
                            <span className="font-bold text-tk-red">
                              ₹{(calcCommission * 25).toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={handleCopyLink}
                          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-tk-red py-3 text-xs font-medium text-white transition hover:bg-tk-red-2"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          {copiedLink ? "Link Copied!" : "Copy Your Referral Link to Start"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 4: MARKETING & PITCH KIT */}
              {activeTab === "marketing" && (
                <div className="mt-6 space-y-6">
                  <div>
                    <h4 className="font-display text-lg font-light text-white">
                      Hospitality Pitch Collateral
                    </h4>
                    <p className="text-xs text-bone/60">
                      Pre-written introductions tailored for different restaurant operators. Click "Copy Pitch" to immediately paste into WhatsApp, email, or LinkedIn.
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Chef Buddy Pitch */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xs font-medium uppercase tracking-wider text-tk-red">
                          Product Card 1 · Chef Buddy
                        </span>
                        <button
                          onClick={() =>
                            handleCopyPitch(
                              "chefbuddy",
                              `Have you seen That Korner's Chef Buddy? It's a digital conversational menu concierge that answers guest questions about ingredients, allergens, pairings, and chef specials in 60+ languages right from the table QR. Check it out here: ${profile.referralLink}`
                            )
                          }
                          className="flex items-center gap-1 text-xs text-bone/70 hover:text-white"
                        >
                          {copiedPitch === "chefbuddy" ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          {copiedPitch === "chefbuddy" ? "Copied" : "Copy Pitch"}
                        </button>
                      </div>
                      <h5 className="mt-2 text-sm font-medium text-white">Give Every Menu a Voice</h5>
                      <p className="mt-1 text-xs text-bone/70">
                        "Chef Buddy helps restaurant guests explore menus, discover dishes, understand ingredients, and receive relevant recommendations through a conversational digital menu experience."
                      </p>
                    </div>

                    {/* Chef Brain Pitch */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xs font-medium uppercase tracking-wider text-tk-red">
                          Product Card 2 · Chef Brain
                        </span>
                        <button
                          onClick={() =>
                            handleCopyPitch(
                              "chefbrain",
                              `Are you analyzing menu performance and dish profitability? That Korner has a tool called Chef Brain that turns live dining interactions into profitability and popularity insights. Take a look here: ${profile.referralLink}`
                            )
                          }
                          className="flex items-center gap-1 text-xs text-bone/70 hover:text-white"
                        >
                          {copiedPitch === "chefbrain" ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          {copiedPitch === "chefbrain" ? "Copied" : "Copy Pitch"}
                        </button>
                      </div>
                      <h5 className="mt-2 text-sm font-medium text-white">Turn Menu Data Into Better Decisions</h5>
                      <p className="mt-1 text-xs text-bone/70">
                        "Chef Brain helps restaurant teams analyse menu performance, understand profitability, and identify opportunities to make more informed menu decisions."
                      </p>
                    </div>

                    {/* Korner Ask Pitch */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xs font-medium uppercase tracking-wider text-tk-red">
                          Product Card 3 · Korner Ask
                        </span>
                        <button
                          onClick={() =>
                            handleCopyPitch(
                              "kornerask",
                              `Want to make guest dining more personalized without complicating floor operations? Korner Ask gives your floor team relevant guest preference insights. Check it here: ${profile.referralLink}`
                            )
                          }
                          className="flex items-center gap-1 text-xs text-bone/70 hover:text-white"
                        >
                          {copiedPitch === "kornerask" ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          {copiedPitch === "kornerask" ? "Copied" : "Copy Pitch"}
                        </button>
                      </div>
                      <h5 className="mt-2 text-sm font-medium text-white">Make Guest Experiences More Personal</h5>
                      <p className="mt-1 text-xs text-bone/70">
                        "Korner Ask supports restaurant teams with relevant guest preference insights, helping them deliver more informed and personalised service."
                      </p>
                    </div>

                    {/* Platform Pitch */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xs font-medium uppercase tracking-wider text-tk-red">
                          Product Card 4 · That Korner Platform
                        </span>
                        <button
                          onClick={() =>
                            handleCopyPitch(
                              "platform",
                              `If you're looking for a restaurant management suite that brings digital workflows, floor management, and conversational menus into one platform, explore That Korner: ${profile.referralLink}`
                            )
                          }
                          className="flex items-center gap-1 text-xs text-bone/70 hover:text-white"
                        >
                          {copiedPitch === "platform" ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          {copiedPitch === "platform" ? "Copied" : "Copy Pitch"}
                        </button>
                      </div>
                      <h5 className="mt-2 text-sm font-medium text-white">Bring Restaurant Operations Together</h5>
                      <p className="mt-1 text-xs text-bone/70">
                        "That Korner provides digital tools designed to support restaurant workflows, from guest-facing experiences to essential operational processes."
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PAYOUT MODAL POPUP */}
        {showPayoutModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowPayoutModal(false)}
            />
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#12151b] p-6 text-bone shadow-2xl">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <h4 className="font-medium text-white">Payout Account Details</h4>
                <button
                  onClick={() => setShowPayoutModal(false)}
                  className="text-bone/60 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {payoutRequested ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
                  <p className="mt-3 text-sm font-medium text-white">
                    Payout Request Submitted!
                  </p>
                  <p className="mt-1 text-xs text-bone/60">
                    Your details have been saved and payout request is queued for processing in the upcoming payout cycle.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSavePayout} className="mt-4 space-y-3 text-xs">
                  <div>
                    <label className="block text-bone/70">Payment Preference</label>
                    <div className="mt-1.5 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPayoutMethod("bank")}
                        className={`flex-1 rounded-lg border py-2 text-center transition ${
                          payoutMethod === "bank"
                            ? "border-tk-red bg-tk-red/10 text-white"
                            : "border-white/10 text-bone/60"
                        }`}
                      >
                        Bank Transfer
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayoutMethod("upi")}
                        className={`flex-1 rounded-lg border py-2 text-center transition ${
                          payoutMethod === "upi"
                            ? "border-tk-red bg-tk-red/10 text-white"
                            : "border-white/10 text-bone/60"
                        }`}
                      >
                        UPI / Instant ID
                      </button>
                    </div>
                  </div>

                  {payoutMethod === "bank" ? (
                    <>
                      <div>
                        <label className="block text-bone/70">Account Holder Name</label>
                        <input
                          type="text"
                          required
                          value={payoutAccountName}
                          onChange={(e) => setPayoutAccountName(e.target.value)}
                          placeholder="e.g. Maya Chen"
                          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-bone/70">Bank Name</label>
                        <input
                          type="text"
                          required
                          value={payoutBankName}
                          onChange={(e) => setPayoutBankName(e.target.value)}
                          placeholder="e.g. HDFC Bank / Emirates NBD"
                          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-bone/70">Account Number / IBAN</label>
                        <input
                          type="text"
                          required
                          value={payoutAccountNumber}
                          onChange={(e) => setPayoutAccountNumber(e.target.value)}
                          placeholder="e.g. 501002349012"
                          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-bone/70">IFSC / Routing / SWIFT Code</label>
                        <input
                          type="text"
                          required
                          value={payoutIfsc}
                          onChange={(e) => setPayoutIfsc(e.target.value)}
                          placeholder="e.g. HDFC0001234"
                          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-white focus:outline-none"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="block text-bone/70">UPI ID / PayPal Email</label>
                      <input
                        type="text"
                        required
                        value={payoutUpiId}
                        onChange={(e) => setPayoutUpiId(e.target.value)}
                        placeholder="username@okhdfcbank or paypal@domain.com"
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-white focus:outline-none"
                      />
                    </div>
                  )}

                  <p className="text-[11px] text-bone/50">
                    *Commissions are disbursed according to the published payout schedule upon meeting the minimum threshold.
                  </p>

                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full rounded-xl bg-tk-red py-2.5 text-xs font-medium text-white transition hover:bg-tk-red-2"
                    >
                      Save Payout Details
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
